import React, { useState, useRef, useEffect } from 'react';
import { IconChevron } from 'gatorcomponents';
import { useResize } from '@/hooks/useResize';
import * as Styles from './PlanCarousel.styles';
import PlanCard from '../PlanCard/PlanCard';

const STEP = 1;

const getVisibleCards = (width) => {
  if (width < 600) return 1;
  if (width < 900) return 2;
  if (width < 1440) return 2;
  return 3;
};

const PlanCarousel = ({
  plans,
  locale,
  isMobile,
  getButtonState,
  onSelectPlan,
}) => {
  const { width } = useResize();
  const visibleCards = getVisibleCards(width);

  const [index, setIndex] = useState(0);
  const viewportRef = useRef(null);

  const maxIndex = Math.max(0, plans.length - visibleCards);
  const canGoPrev = index > 0;
  const canGoNext = index < maxIndex;
  const showNav = plans.length > visibleCards;
  const dotCount = maxIndex + 1;

  useEffect(() => {
    setIndex(prev => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const goTo = next => setIndex(Math.max(0, Math.min(next, maxIndex)));

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => el.style.setProperty('--carousel-vw', `${el.offsetWidth}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Styles.CarouselWrapper>
      <Styles.PlanListViewport ref={viewportRef} showFade={canGoNext && !isMobile} data-testid="vpsoci-upgrade-listing-carousel">
        <Styles.PlanListWrapper index={index} visibleCards={visibleCards} data-testid="vpsoci-upgrade-listing-plan-list">
          {plans.map(plan => (
            <PlanCard
              key={plan.productId}
              plan={plan}
              locale={locale}
              isMobile={isMobile}
              buttonState={getButtonState(plan.productId)}
              onSelect={() => onSelectPlan(plan)}
            />
          ))}
        </Styles.PlanListWrapper>
      </Styles.PlanListViewport>

      {showNav && (
        <Styles.CarouselNav data-testid="vpsoci-upgrade-listing-carousel-nav">
          <Styles.CarouselNavButton
            onClick={() => goTo(index - STEP)}
            disabled={!canGoPrev}
            data-testid="vpsoci-upgrade-listing-carousel-prev"
          >
            <IconChevron color="primaryMedium" direction="left" size="lg" />
          </Styles.CarouselNavButton>
          <Styles.CarouselDots>
            {Array.from({ length: dotCount }, (_, i) => (
              <Styles.CarouselDot
                key={i}
                isActive={i === index}
                onClick={() => goTo(i)}
                data-testid={`vpsoci-upgrade-listing-carousel-dot-${i}`}
              />
            ))}
          </Styles.CarouselDots>
          <Styles.CarouselNavButton
            onClick={() => goTo(index + STEP)}
            disabled={!canGoNext}
            data-testid="vpsoci-upgrade-listing-carousel-next"
          >
            <IconChevron color="primaryMedium" direction="right" size="lg" />
          </Styles.CarouselNavButton>
        </Styles.CarouselNav>
      )}
    </Styles.CarouselWrapper>
  );
};

export default PlanCarousel;
